import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { ApiNodeDataIdentityDocument, FillIdData, IdDocInput, IdDocInputKind, IdDocOutputKind } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Tooltip } from '@onefootprint/ui';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../base-node';
import OutputMappings from '../../shared/output-mappings';
import { acceptsSomeDocument, expectedInputSides, getDocumentScope, type InputDocumentSide, toDocumentTypesAndCountries } from '../id-doc-kinds';
import AcceptedDocumentsSection from './accepted-documents-section';
import DocumentInputsSection from './document-inputs-section';
import type { FormData } from './id-docs.types';
type IdDocsFormProps = {
  node: NodeProps<ApiNodeDataIdentityDocument>;
  mutation: ApplyEditMutation;
  isFirmEmployee: boolean;
};

/** The backend requires a data key for both of these, so their rows are always shown. */
const PINNED_OUTPUT_KINDS: IdDocOutputKind[] = ['risk_signals', 'ocr_data'];
const INPUT_KIND_BY_SIDE: Record<InputDocumentSide, IdDocInputKind> = {
  front: 'front_image',
  back: 'back_image',
  selfie: 'selfie_image'
};
const IdDocsForm = ({
  node: {
    id,
    data: {
      documentData,
      collectSelfie,
      documentTypesAndCountries: {
        global,
        countrySpecific
      },
      uploadSettings,
      matchType,
      inputs,
      outputs,
      fillIdData
    }
  },
  mutation,
  isFirmEmployee
}: IdDocsFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'collectDocument'
  });
  const {
    t: tKinds
  } = useTranslation('playbook-builder', {
    keyPrefix: 'collectDocument.outputs.kinds'
  });
  const [isCountryPickerActive, setIsCountryPickerActive] = useState(false);
  const {
    toggle,
    toggleBlocking
  } = useDirty();
  // Hard-coded: the set of output values is fixed by IdDocOutputKind.
  const outputKinds: {
    value: IdDocOutputKind;
    label: string;
    description?: string;
  }[] = [{
    value: 'age',
    label: tKinds($ => $.age)
  }, {
    value: 'dob',
    label: tKinds($_0 => $_0.dob)
  }, {
    value: 'issuing_country',
    label: tKinds($_1 => $_1.issuingCountry)
  }, {
    value: 'risk_signals',
    label: tKinds($_2 => $_2.riskSignals)
  }, {
    value: 'ocr_data',
    label: tKinds($_3 => $_3.ocrData)
  }, {
    value: 'device_insights',
    label: tKinds($_4 => $_4.deviceInsights),
    description: t($_5 => $_5.outputs.descriptions.deviceInsights)
  }, {
    value: 'document_authenticity_check',
    label: tKinds($_6 => $_6.documentAuthenticityCheck),
    description: t($_7 => $_7.outputs.descriptions.documentAuthenticityCheck)
  }];

  // Hard-coded: the set of values is fixed by FillIdData.
  const fillIdDataOptions: {
    value: FillIdData;
    label: string;
  }[] = [{
    value: 'never',
    label: t($_8 => $_8.fillIdData.options.never)
  }, {
    value: 'when_empty',
    label: t($_9 => $_9.fillIdData.options.whenEmpty)
  }];
  const form = useForm<FormData>({
    defaultValues: {
      scope: getDocumentScope({
        global
      }),
      collectSelfie,
      country: countrySpecific,
      global,
      requireMobileCapture: uploadSettings === 'require_mobile_capture',
      useFuzzyNameMatch: matchType === 'fuzzy',
      fillIdData,
      // Tolerate a missing field: a backend that predates node outputs omits it entirely.
      outputs: [...PINNED_OUTPUT_KINDS.map(kind => ({
        fieldKey: kind,
        onboardingDataKey: (outputs ?? []).find(output => output.kind === kind)?.onboardingDataKey ?? ''
      })), ...(outputs ?? []).filter(({
        kind: kind_0
      }) => !PINNED_OUTPUT_KINDS.includes(kind_0)).map(({
        kind: kind_1,
        onboardingDataKey
      }) => ({
        fieldKey: kind_1,
        onboardingDataKey
      }))],
      useInputs: inputs.length > 0,
      inputs: {
        front: inputs.find(input => input.kind === 'front_image')?.expression ?? '',
        back: inputs.find(input_0 => input_0.kind === 'back_image')?.expression ?? '',
        selfie: inputs.find(input_1 => input_1.kind === 'selfie_image')?.expression ?? ''
      }
    }
  });
  const {
    register,
    handleSubmit,
    watch
  } = form;
  const countryRestrictions = watch('country');
  const selectedFillIdData = watch('fillIdData');
  useEffect(() => {
    const isCountryDirty = !isEqual(countryRestrictions, countrySpecific);
    toggle('identity-document-form', form.formState.isDirty || isCountryDirty || isCountryPickerActive);
    toggleBlocking('identity-document-form', isCountryPickerActive);
  }, [form.formState.isDirty, isCountryPickerActive, countryRestrictions]);
  const handleFormSubmit = async (data: FormData) => {
    if (!acceptsSomeDocument(data)) {
      return;
    }
    try {
      await mutation.mutateAsync({
        kind: 'update_node',
        nodeId: id,
        nodeData: {
          kind: 'identity_document',
          documentData,
          uploadSettings: data.requireMobileCapture ? 'require_mobile_capture' : 'prefer_capture',
          documentTypesAndCountries: toDocumentTypesAndCountries(data),
          collectSelfie: data.collectSelfie,
          matchType: data.useFuzzyNameMatch ? 'fuzzy' : 'exact',
          fillIdData: data.fillIdData,
          outputs: data.outputs.map(({
            fieldKey,
            onboardingDataKey: onboardingDataKey_0
          }) => ({
            kind: fieldKey as IdDocOutputKind,
            onboardingDataKey: onboardingDataKey_0
          })),
          inputs: toInputs(data)
        }
      });
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  return <FormProvider {...form}>
      <form id="id-document-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          <DocumentInputsSection nodeId={id} />
          <AcceptedDocumentsSection setIsCountryPickerActive={setIsCountryPickerActive} />
          <section className="flex flex-col gap-3">
            <div className="text-label-2 text-primary">{t($_10 => $_10.extraRequirements)}</div>
            <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">
              <Form.Checkbox label={t($_11 => $_11.requestSelfie)} {...register('collectSelfie')} />
              <Divider />
              <Form.Toggle<FormData> name="requireMobileCapture" hint={t($_12 => $_12.requireMobileCaptureDescription)} label={t($_13 => $_13.requireMobileCapture)} size="compact" />
              <Divider />
              <Form.Toggle<FormData> name="useFuzzyNameMatch" hint={t($_14 => $_14.enableFuzzyMatchingDescription)} label={t($_15 => $_15.enableFuzzyMatching)} size="compact" />
            </div>
          </section>
          <OutputMappings canEdit subtitle={t($_16 => $_16.outputs.subtitle)} options={outputKinds} outputLabel={t($_17 => $_17.outputs.outputLabel)} pinnedFieldKeys={PINNED_OUTPUT_KINDS} />
          {isFirmEmployee && <section className="flex flex-col gap-3">
              <div className="text-label-2 text-primary">{t($_18 => $_18.fillIdData.title)}</div>
              <div className="flex flex-col gap-2 p-3 border border-tertiary rounded-md">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-label-3 text-primary">{t($_19 => $_19.fillIdData.label)}</span>
                  <Tooltip content={t($_20 => $_20.fillIdData.employeeOnly)} side="left">
                    <div className="flex-shrink-0 text-tertiary">
                      <IcoEyeCrossed16 />
                    </div>
                  </Tooltip>
                </div>
                <Form.Select<FormData> name="fillIdData" aria-label={t($_21 => $_21.fillIdData.label)} size="compact" options={fillIdDataOptions} />
                <p className="text-caption-2 text-tertiary">
                  {selectedFillIdData === 'when_empty' ? t($_22 => $_22.fillIdData.descriptions.whenEmpty) : t($_23 => $_23.fillIdData.descriptions.never)}
                </p>
              </div>
            </section>}
        </div>
      </form>
    </FormProvider>;
};

/**
 * Only the sides the node's own settings ask for: a side past those is one nothing will ever read,
 * which the backend rejects rather than ignores.
 */
const toInputs = (data: FormData): IdDocInput[] => {
  if (!data.useInputs) {
    return [];
  }
  const sides = expectedInputSides({
    ...toDocumentTypesAndCountries(data),
    collectSelfie: data.collectSelfie
  });
  return sides.map(side => ({
    kind: INPUT_KIND_BY_SIDE[side],
    expression: data.inputs[side]
  }));
};
export default IdDocsForm;